import { Formik } from "formik";
import React, { useContext, useEffect } from "react";
import * as yup from "yup";
import { BackButton } from "../../components/BackButton";
import YearSelect, { DateOptions } from "../../components/YearSelect";
import { Step } from "../../models/Step";
import { ApplicationFormStateContext } from "../../state/applicationFormState";
import { saveStateRemote } from "../../utils/api";
import { trackEvent } from "../../utils/gtm";
import { scrollToTopOfContent } from "../../utils/scroll";
import { useNavigate } from "react-router-dom";


const EmploymentStep: React.FunctionComponent = () => {
  const [state, dispatch] = useContext(ApplicationFormStateContext);
  const navigation = useNavigate();
  const { employmentType, employmentYear } = state;

  enum EmploymentTypeSubType {
    Job,
    Education,
    Other,
  }

  const employmentTypeOptions = [
    {
      value: "WHITE_COLLAR_WORKER",
      label: "Funktionær/Lønmodtager",
      subtype: EmploymentTypeSubType.Job,
    },
    {
      value: "PAID_BY_HOUR",
      label: "Timelønnet",
      subtype: EmploymentTypeSubType.Job,
    },
    {
      value: "PRE_PENSIONER",
      label: "Førtidspensionist",
      subtype: EmploymentTypeSubType.Other,
    },
    {
      value: "PENSIONAR",
      label: "Pensionist",
      subtype: EmploymentTypeSubType.Other,
    },
    {
      value: "STAYING_AT_HOME",
      label: "Hjemmegående",
      subtype: EmploymentTypeSubType.Other,
    },
    {
      value: "STUDENT",
      label: "Studerende",
      subtype: EmploymentTypeSubType.Education,
    },
    {
      value: "TRAINEE",
      label: "Elev/Lærling",
      subtype: EmploymentTypeSubType.Education,
    },
    { value: "MANAGER", label: "Direktør", subtype: EmploymentTypeSubType.Job },
    {
      value: "EGEN_FORETAGARE",
      label: "Selvstændig",
      subtype: EmploymentTypeSubType.Job,
    },
    {
      value: "VIKARIAT",
      label: "Midlertidigt job",
      subtype: EmploymentTypeSubType.Job,
    },
    {
      value: "ARBETSLOS",
      label: "Ledig",
      subtype: EmploymentTypeSubType.Other,
    },
    {
      value: "OTHER",
      label: "Anden ansættelse",
      subtype: EmploymentTypeSubType.Job,
    },
  ];

  useEffect(() => {
    trackEvent({eventName: 'step_load', data: { step: 'Employment' } });
    dispatch({ type: "set_step", step: Step.Employment });
    scrollToTopOfContent();

    const saveState = async () => {
      await saveStateRemote(state);
    };
    saveState();
  }, []);

  const schema = yup.object({
    employmentType: yup.string().required("Vælg ansættelsesforhold"),
    employmentYear: yup
      .string()
      .required("Vælg hvornår blev du ansat i nuværende job"),
  });

  return (
    <>
      <h2>Ansættelsesforhold</h2>

      <Formik
        initialValues={{
          employmentType,
          employmentYear,
        }}
        validationSchema={schema}
        onSubmit={(values) => {
          dispatch({
            type: "set_employmentType",
            values: values.employmentType,
          });

          dispatch({
            type: "set_employmentYear",
            values: values.employmentYear,
          });

          navigation("/indkomst");
        }}
      >
        {(formikProps) => (
          <div className="mt-5">
            <div className="mt-5">
              <label className="label-default">Ansættelsesforhold</label>
              <select
                name="employmentType"
                value={formikProps.values.employmentType}
                onChange={formikProps.handleChange}
              >
                <option value="">Vælg ansættelsesforhold</option>
                {employmentTypeOptions.map((employmentType) => (
                  <option
                    key={employmentType.value}
                    value={employmentType.value}
                  >
                    {employmentType.label}
                  </option>
                ))}
              </select>

              {(formikProps.touched.employmentType ||
                formikProps.submitCount > 0) &&
                formikProps.errors.employmentType && (
                  <div className="invalid-feedback">
                    {formikProps.errors.employmentType}
                  </div>
                )}
            </div>

            <div className="mt-5">
              <label className="label-default">
                Hvornår blev du ansat/selvstændig i nuværende job
              </label>
              <YearSelect
                dateOptions={DateOptions.PastDatesInclusive}
                text="Vælg årstal"
                value={formikProps.values.employmentYear?.getFullYear()}
                onChange={(value) => {
                  const date: Date | undefined = value
                    ? new Date(Date.UTC(value, 0, 1))
                    : undefined;
                  formikProps.setFieldValue("employmentYear", date, true);
                }}
              />

              {(formikProps.touched.employmentYear ||
                formikProps.submitCount > 0) &&
                formikProps.errors.employmentYear && (
                  <div className="invalid-feedback">
                    {formikProps.errors.employmentYear}
                  </div>
                )}
            </div>

            <div className="container-btn">
              <BackButton />

              <button
                className="btn-next"
                type="submit"
                onClick={() => formikProps.submitForm()}
              >
                Næste
              </button>
            </div>
          </div>
        )}
      </Formik>
    </>
  );
};

export default EmploymentStep;
